import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  CreditCardIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { BillingInfo } from "../../../types/billing";

interface BillingOverviewProps {
  billingData: BillingInfo;
  onPrimaryAction: () => void;
  isExpanded: boolean;
}

export const BillingOverview: React.FC<BillingOverviewProps> = ({
  billingData,
  onPrimaryAction,
  isExpanded,
}) => {
  const getPlanDisplayName = (planType: string): string => {
    const names: Record<string, string> = {
      free: "Free",
      starter: "Starter",
      professional: "Professional",
      enterprise: "Enterprise",
    };
    return names[planType?.toLowerCase()] || planType;
  };

  const getStatusBadge = () => {
    if (billingData.is_trial_active) {
      const daysLeft = Math.ceil(
        (new Date(billingData.trial_end_date!).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24),
      );
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <ClockIcon className="w-3 h-3 mr-1" />
          Trial - {daysLeft} days left
        </span>
      );
    }

    if (billingData.cancel_at_period_end) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          <ExclamationCircleIcon className="w-3 h-3 mr-1" />
          Canceling
        </span>
      );
    }

    if (billingData.scheduled_changes) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <ClockIcon className="w-3 h-3 mr-1" />
          Change Scheduled
        </span>
      );
    }

    if (billingData.subscription_status === "active") {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    }

    if (billingData.plan_type === "free") {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Free Plan
        </span>
      );
    }

    return null;
  };

  const getPrimaryActionLabel = (): string => {
    if (billingData.plan_type === "free") return "Upgrade to Paid Plan";
    if (billingData.is_trial_active) return "Choose Your Plan";
    if (isExpanded) return "Hide Details";
    return "Manage Subscription";
  };

  const formatBillingCycle = (cycle?: string): string => {
    if (!cycle) return "";
    return cycle.charAt(0).toUpperCase() + cycle.slice(1);
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Calculate monthly cost for display
  const getMonthlyPrice = (): string | null => {
    if (billingData.billing_type === "manual") return "Custom Pricing";
    if (billingData.plan_type === "free") return "Free";
    // Price preview is shown in a modal before checkout; avoid stale/hardcoded figures here
    return null;
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Left Section - Plan Information */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-2xl font-bold text-gray-900">
              {getPlanDisplayName(billingData.plan_type)} Plan
            </h2>
            {getStatusBadge()}
          </div>

          {/* Key Information Line */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            {billingData.billing_cycle && (
              <span className="flex items-center">
                <CreditCardIcon className="w-4 h-4 mr-1" />
                {formatBillingCycle(billingData.billing_cycle)} billing
              </span>
            )}

            {billingData.current_editors > 0 && (
              <span>
                {billingData.current_editors} editor
                {billingData.current_editors !== 1 ? "s" : ""}
              </span>
            )}

            {billingData.current_period_end &&
              !billingData.cancel_at_period_end && (
                <span>
                  Next billing: {formatDate(billingData.current_period_end)}
                </span>
              )}

            {billingData.cancel_at_period_end &&
              billingData.current_period_end && (
                <span className="text-amber-600 font-medium">
                  Cancels: {formatDate(billingData.current_period_end)}
                </span>
              )}
          </div>

          {/* Scheduled Changes Preview */}
          {billingData.scheduled_changes && (
            <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-900">
                <span className="font-medium">Scheduled Change:</span>{" "}
                {billingData.scheduled_changes.new_plan}
                {billingData.scheduled_changes.new_billing_cycle &&
                  ` (${formatBillingCycle(billingData.scheduled_changes.new_billing_cycle)})`}{" "}
                starting{" "}
                {formatDate(billingData.scheduled_changes.effective_date)}
              </p>
            </div>
          )}
        </div>

        {/* Right Section - Primary Action */}
        <div className="flex flex-col items-end gap-2">
          {getMonthlyPrice() && (
            <div className="text-right mb-2">
              <p className="text-2xl font-bold text-gray-900">
                {getMonthlyPrice()}
              </p>
              <p className="text-xs text-gray-500">per month</p>
            </div>
          )}

          <button
            onClick={onPrimaryAction}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {getPrimaryActionLabel()}
            {isExpanded ? (
              <ChevronUpIcon className="ml-2 -mr-1 h-4 w-4" />
            ) : (
              <ChevronDownIcon className="ml-2 -mr-1 h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
